/**
 * REFACTOR-SPLIT (VAL-PATH P6s): 588 -> 316 lines. Not truncation: loads and
 * writes moved to _useSignOffRecord.ts, the gate to _signOffGateState.ts.
 * Screen 5 of 5 — SIGN-OFF AND FINALIZE.
 *
 * VALUATION-B3 gave it a real engagement-level attestation record; Finalize
 * writes to that.
 *
 * What actually happens when Finalize is pressed:
 *
 *   1. `POST .../finalize` re-runs the engine on the reviewed inputs, refuses
 *      the whole thing if any blocker stands (incomplete questionnaire,
 *      unresolved out-of-range override, tuned config with no rationale), and
 *      otherwise freezes a `ValuationSnapshot` — inputs, outputs, AND the
 *      methodology config that produced them.
 *   2. The signature comes off the verified token. `signed_by` is not on the
 *      wire, because a sign-off a caller can address to somebody else is not a
 *      sign-off.
 *   3. Only then does `can_produce_document` go true.
 *
 * Three rules this screen renders rather than decides, all enforced server-side:
 *
 *   * **Preview flags, finalize fences.** `/recompute` reports an out-of-range
 *     override at the value the CPA stated and widens the band. `/finalize`
 *     will not freeze it. The blocker list here comes from `/preflight`, which
 *     runs the same gate without writing anything.
 *   * **Re-finalize supersedes, it never overwrites.** A new version is
 *     written, the prior is marked superseded and retained, and only one is
 *     live — "no two live valuations that disagree."
 *   * **A record must still reproduce.** Every read replays the frozen inputs
 *     through the frozen config; the verdict is rendered on screen.
 *
 * The inputs are the SAVED draft, not a browser cache: the server signs what
 * is saved; finalize sends only the sign-off reason. Nothing saved -> Intake.
 */
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { StepFooter } from '@/components/valuation/StepFooter'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/contexts/AuthContext'
import { KEY_PEOPLE_BLOCK_TEXT, SignOffGates, useAgreementNamedNobody } from './_SignOffGates'
import { FinalizeCard, START_NEW_VERSION, isDelivered } from './_FinalizeCard'
import { ProduceDocumentCard } from './_ProduceDocumentCard'
import { PendingReview, FrozenReview } from './_SignOffReview'
import { SnapshotHistory, VerificationPanel } from './_SnapshotHistory'
import { SnapshotDownloadButton } from './_SnapshotDownload'
import { OWNER_ESTIMATE_REASON, OwnerReportCard, RECORD_AND_ORDER } from './_OwnerReportCard'
import { deriveSignOffGates } from './_signOffGateState'
import { useValuationDraft } from './_valuationDraft'
import { useSignoffReason } from './_useDraftExtras'
import { useSignOffRecord } from './_useSignOffRecord'
import { bodyFromDraft } from './_valuationTuningHelpers'
import { DeliverableCard, StatusCard } from './_SignOffStatus'
import { stepBack } from './_steps'
import { SignOffActionBar } from './_SignOffActionBar'
import { SignDeclarations } from './_SignDeclarations'
import { OwnerFiguresReview } from './_OwnerFiguresReview'
import { humanizeChangedField } from './_signOffReissue'
import {
  StatusBadge,
  ValuationStepper,
  ValueKindBadge,
  useCachedRun,
  useValuationEntitlement,
} from './_shared'

export default function ValuationSignOff() {
  const { valuationId } = useParams<{ valuationId: string }>()
  const navigate = useNavigate()
  const { hash } = useLocation()
  const { user } = useAuth()
  const { canConclude } = useValuationEntitlement()
  const run = useCachedRun(valuationId)
  const {
    draft, draftTuning, draftExtras, updatedAt, savedAtLabel, resumedAtLabel, loading: draftLoading, save,
  } = useValuationDraft(valuationId)
  // The review panel's view of what is saved; the server never receives it.
  const tuningBody = useMemo(
    () => (draft ? bodyFromDraft(draft, draftTuning) : null),
    [draft, draftTuning],
  )
  const hasSavedDraft = !!tuningBody
  // VAL-PATH-P6b: the owner records with a fixed reason and never sees the reason box or the Status card.
  const isOwner = user?.role === 'valuation_owner'

  const [rationale, setRationale, saveReason] = useSignoffReason(valuationId, draftExtras.signoff_reason ?? undefined, hasSavedDraft)
  const {
    engagement, loadError, saving, config, snapshot, history, preflight, preflightError,
    finalizing, finalizeRefusal, producing, documents, pdfStatus, setStatus, doFinalize, handleProduce,
  } = useSignOffRecord(valuationId, hasSavedDraft, updatedAt, rationale, setRationale, canConclude)
  // null = the user has not touched it: open by default when inputs changed since the signed version (state B).
  const [newVersionOpen, setNewVersionOpen] = useState<boolean | null>(null)
  // VAL-PATH-P7c: the signer's Full report step is /signoff#report — scroll there once loaded.
  const onReport = !isOwner && hash === '#report'
  const loaded = !!engagement
  useEffect(() => {
    if (onReport && loaded) document.getElementById('report')?.scrollIntoView?.()
  }, [onReport, loaded])

  // Hoisted above the early returns: hooks below must run on every render.
  const frozen = snapshot?.snapshot ?? null
  const liveVersion = frozen && frozen.status === 'final' ? frozen.version : null

  // VAL-REISSUE-SERVER-1: the server's answer, from preflight — only
  // meaningful on a re-finalize (a live version exists).
  const reissueDelta = liveVersion !== null ? preflight?.reissue ?? null : null

  const changedFieldLabels = useMemo(() => {
    if (!reissueDelta || !frozen) return []
    const candidateAnswers = tuningBody?.driver_answers ?? {}
    return reissueDelta.changed_fields.map(key =>
      humanizeChangedField(key, frozen.inputs as Record<string, unknown>, candidateAnswers),
    )
  }, [reissueDelta, frozen, tuningBody])
  // WALKFIX-B F16: the agreement sentence only when an agreement was read and named no one.
  const agreementNamedNobody = useAgreementNamedNobody(
    valuationId, !!preflight?.blockers.includes(KEY_PEOPLE_BLOCK_TEXT))

  if (loadError) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{loadError}</div>
      </div>
    )
  }
  if (!engagement) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-6">
        <div>
          <Skeleton className="h-8 w-64" />
          <Skeleton className="mt-2 h-4 w-40" />
        </div>
        <Skeleton className="h-10 w-full" />
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardHeader><Skeleton className="h-5 w-40" /></CardHeader>
            <CardContent className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  // The in-session band (`preflight.computed`). The gate and Record key off
  // THIS, not `hasBand`: pre-finalize no persisted band exists (VAL-STATE-PERSIST-1 R1).
  const computed = preflight?.computed ?? null
  const hasComputedValue = computed !== null && computed.value_low !== null && computed.value_high !== null

  const {
    concluded, ownerRecordedFinal, low, high, hasBand,
    reissueFingerprintPending, methodologyReason, awaitingSignature, gateOk, canFinalize, blockedReason,
  } = deriveSignOffGates({
    engagement, frozen, liveVersion, preflight, hasComputedValue, reissueDelta,
    tuningBody, draftTuning, rationale: isOwner ? OWNER_ESTIMATE_REASON : rationale, canConclude,
  })

  const delivered = isDelivered(liveVersion, snapshot?.documents_on_file ?? false, snapshot?.can_produce_document ?? false)
  const inputsChanged = !reissueFingerprintPending && reissueDelta?.changed === true
  const cardOpen = newVersionOpen ?? (inputsChanged || awaitingSignature)
  const ownerRecorded = liveVersion !== null && !inputsChanged
  // The bar's Re-finalize as v{N+1} button: open the new-version card, scroll to it, focus the reason.
  function openNewVersion() {
    setNewVersionOpen(true)
    setTimeout(() => {
      const box = document.getElementById('signoff-rationale')
      box?.scrollIntoView?.({ block: 'center' })
      box?.focus()
    }, 0)
  }
  const finalizeCard = (
    <FinalizeCard
      methodologyReason={methodologyReason}
      rationale={rationale}
      onRationaleChange={setRationale}
      canFinalize={canFinalize}
      blockedReason={blockedReason}
      finalizing={finalizing}
      onFinalize={() => void doFinalize()}
      liveVersion={liveVersion}
      willSign={canConclude}
      reissueChanged={reissueFingerprintPending || awaitingSignature ? null : (reissueDelta?.changed ?? null)}
      changedFieldLabels={changedFieldLabels}
      delivered={delivered}
      tuned={!!preflight?.tuned}
    />
  )

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{engagement.subject_company_name}</h1>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-500">
          <ValueKindBadge kind={engagement.value_kind} />
          <StatusBadge status={engagement.status} />
        </div>
      </div>

      <ValuationStepper current={onReport ? 'report' : 'signoff'} engagementId={valuationId} role={user?.role} />

      {/* VAL-PATH-ROUTES (P1): the signer's "Full report" step links here (/signoff#report). */}
      <div id="report">
        <DeliverableCard
          engagement={engagement}
          low={low}
          high={high}
          hasBand={hasBand}
          // A signed value shows the signed wording; the cached preview text is only for a preview.
          positioning={frozen ? snapshot?.statement : run?.positioning}
          previewLow={computed?.value_low}
          previewHigh={computed?.value_high}
          owner={isOwner}
        />
      </div>

      <SignOffGates
        hasComputedValue={hasComputedValue}
        concluded={concluded}
        canConclude={canConclude}
        userName={user?.name}
        userRole={user?.role}
        hasTuningInputs={hasSavedDraft || draftLoading}
        preflight={preflight}
        preflightError={preflightError}
        onBack={() => navigate(stepBack('signoff', user?.role, valuationId ?? '') ?? `/valuation/${valuationId}/output`)}
        liveVersion={liveVersion}
        engagementId={valuationId}
        agreementNamedNobody={agreementNamedNobody}
      />

      {/* The four things a reader of the deliverable would challenge later:
          the weights, the reason they moved, the driver scores, the benchmark
          version. Before the signature they are a preview; after it they are
          read back out of the frozen record. */}
      {draftLoading && <p className="text-sm text-gray-500">Loading your saved inputs…</p>}
      {tuningBody && (savedAtLabel || resumedAtLabel) && (
        <p className="text-sm text-gray-500">{savedAtLabel ?? `Saved draft from ${resumedAtLabel}`}</p>
      )}
      {/* WALKFIX-B F13: the owner never signs, so the signer's review stays off their screen. */}
      {tuningBody && !isOwner && (
        <PendingReview
          body={tuningBody}
          basis={preflight}
          computed={preflight?.computed ?? null}
          config={config}
        />
      )}

      {!isOwner && !delivered && valuationId && <OwnerFiguresReview engagementId={valuationId} />}
      {!isOwner && !delivered && (
        <SignDeclarations engagement={engagement} computed={computed} savedAck={draft?.market_only_acknowledged ?? false} save={save} />
      )}

      {isOwner
        ? <OwnerReportCard engagement={engagement} recorded={ownerRecorded} snapshotOnFile={!!frozen && ownerRecordedFinal} saving={saving} onSend={() => void setStatus('in_cpa_review')} />
        : !delivered && finalizeCard}

      {frozen && <FrozenReview collapsible snapshot={frozen} valueKind={engagement.value_kind} signedByName={snapshot?.signed_by_name} multipleSourceLabel={snapshot?.multiple_source_label} benchmarkSourceLabel={snapshot?.benchmark_source_label} />}
      {snapshot?.verification && <VerificationPanel verification={snapshot.verification} />}

      <ProduceDocumentCard
        engagementId={valuationId ?? ''}
        liveVersion={liveVersion}
        documentsOnFile={snapshot?.documents_on_file ?? false}
        concluded={concluded}
        canProduce={snapshot?.can_produce_document ?? false}
        note={
          user?.role === 'valuation_owner' && frozen && !engagement.full_report_paid
            ? 'Your estimate is recorded. You prepared it yourself, so it is an estimate, not a signed valuation.' +
              ' Your report will be available after payment.'
            // WALKFIX-B F13: the server's lock line speaks of the valuator's weights — signer-only.
            : isOwner && !frozen
              ? 'Finalize the valuation to unlock document production.'
              : snapshot?.production_note ?? 'Finalize the valuation to unlock document production.'
        }
        onProduce={handleProduce}
        producing={producing}
        documents={documents}
        pdfStatus={pdfStatus}
      />

      {delivered && !isOwner && (
        <div>
          <button
            type="button"
            className="text-sm text-gray-600 underline underline-offset-2 hover:text-gray-900"
            aria-expanded={cardOpen}
            onClick={() => setNewVersionOpen(!cardOpen)}
          >
            {START_NEW_VERSION}
          </button>
          {cardOpen && <div className="mt-3">{finalizeCard}</div>}
        </div>
      )}

      {/* The owner's Snapshot download lives in OwnerReportCard (one home). */}
      {frozen && ownerRecordedFinal && (user?.role === 'valuation_signer' || user?.role === 'super_admin')
        && engagement.snapshot_available && (
        <SnapshotDownloadButton engagementId={valuationId ?? ''} />
      )}

      <SnapshotHistory rows={history} collapsible />

      {!isOwner && (
        <StatusCard
          collapsible
          engagement={engagement}
          canConclude={canConclude}
          saving={saving}
          onSetStatus={setStatus}
        />
      )}

      {/* The owner's final action is the footer's (U1); the sticky bar would repeat it. */}
      {/* POL-B item 1: why signing was refused (a set-aside approach has no reason). */}
      {!isOwner && finalizeRefusal && <p role="alert" className="text-sm text-red-700">{finalizeRefusal}</p>}
      {!isOwner && <SignOffActionBar
        engagementId={valuationId ?? ''}
        hasComputedValue={hasComputedValue}
        concluded={concluded}
        canConclude={canConclude}
        engineOk={preflight ? preflight.ok : null}
        liveVersion={liveVersion}
        willSign={canConclude}
        reissueChanged={reissueFingerprintPending || awaitingSignature ? null : (reissueDelta?.changed ?? null)}
        awaitingSignature={awaitingSignature}
        finalizing={finalizing}
        canFinalize={canFinalize}
        gateOk={gateOk}
        blockedReason={blockedReason}
        onFinalize={() => void doFinalize()}
        documentsOnFile={snapshot?.documents_on_file ?? false}
        canProduce={snapshot?.can_produce_document ?? false}
        producing={producing}
        onProduce={handleProduce}
        documents={documents}
        changedFieldLabels={changedFieldLabels}
        onOpenNewVersion={openNewVersion}
      />}

      <StepFooter
        step="signoff"
        engagementId={valuationId ?? ''}
        role={user?.role}
        save={saveReason}
        final={isOwner && !ownerRecorded
          ? { label: RECORD_AND_ORDER, onClick: () => void doFinalize(OWNER_ESTIMATE_REASON), disabled: !gateOk || finalizing }
          : undefined}
      />
    </div>
  )
}
